/**
 * Route copy for `src/routes/workflows` (keys: `routes.workflows.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  crmAutomationsView: {
    fieldName: 'field {{field}}',
    draft: 'draft',
    stageV: 'stage {{v}}',
    everyNewContact: 'every new contact',
    fieldNotSet: 'field not set',
    toAStage: 'to a stage',
    everyStageChange: 'every stage change',
    vDWithoutReply: '{{v}} d without reply',
    aiResearch: 'AI research',
    owner: 'owner?',
    selectedGroup: 'selected group',
    group: 'group?',
    emptyMessage: 'empty message',
    emptySubject: 'empty subject',
    trigger: 'Trigger',
    action: 'Action',
    triggers: 'Triggers',
    dragATriggerThenActionsConnect: 'Drag a trigger, then actions, connect them, then Save.',
    stageFilterOptional: 'Stage (filter, optional)',
    all: '— all —',
    sourceFilterOptional: 'Source (filter, optional)',
    inboxImportManual: 'inbox, import, manual…',
    entity: 'Entity',
    contact: 'Contact',
    company: 'Company',
    deal: 'Deal',
    watchedField: 'Watched field',
    lifecycleStageOwnerUserIdCustom: 'lifecycle_stage, owner_user_id, custom.xxx…',
    pipelineOptional: 'Pipeline (optional)',
    all2: '— all —',
    toTheStageOptional: 'To the stage (optional)',
    anyStage: '— any stage —',
    firstChooseAPipeline: 'First choose a pipeline.',
    noReplyDelayDays: 'No-reply delay (days)',
    channelOptional: 'Channel (optional)',
    any: '— any —',
    email: 'Email',
    social: 'Social',
    enrichesTheContactWithAiResearch: 'Enriches the contact with AI (research + pinned note). No configuration: the trigger\'s contact is enriched.',
    strategy: 'Strategy',
    manualSpecificUser: 'Manual (specific user)',
    roundRobin: 'Round-robin',
    ownerUserId: 'Owner (user id)',
    user: 'user-…',
    existingGroup: 'Existing group',
    choose: '— choose —',
    orCreateAGroupName: '…or create a group (name)',
    newLeads: 'New leads',
    message: 'Message',
    newContactContactNameToHandle: 'New contact {contact.name} to handle.',
    subject: 'Subject',
    gladToHaveYouWithUs: 'Glad to have you with us',
    bodyOptional: 'Body (optional)',
    helloContactFirstName: 'Hello {contact.first_name}…',
    channel: 'Channel',
    directMessage: 'Direct message',
    instructionsOptional: 'Instructions (optional)',
    warmToneOfferA15Min: 'Warm tone, offer a 15-min call.',
    generatesADraftHumanReviewBefore: 'Generates a draft (human review before sending).',
    configuration: 'Configuration',
    selectANodeToEditIt: 'Select a node to edit it, or drag a trigger / an action from the palette.',
    delete: 'Delete',
    name: 'Name',
    id: 'id:',
    giveTheAutomationAName: 'Give the automation a name.',
    couldNotSave: 'Could not save.',
    automations: '← Automations',
    automationName: 'Automation name',
    enabled: 'Enabled',
    disabled: 'Disabled',
    unsavedChanges: 'unsaved changes',
    validate: 'Validate',
    saving: 'Saving…',
    save: 'Save',
    runningCrmAutomationsDependsOnA: 'Running CRM automations depends on a dedicated backend engine (not included). You can compose, validate, save and enable/disable the rule here.',
    automationValid: '✓ Automation valid.',
    lengthErrorS: '{{length}} error(s):',
    backspace: 'Backspace',
    delete2: 'Delete',
    couldNotDelete: 'Could not delete.',
    deleteTheAutomation: 'Delete the automation',
    cancel: 'Cancel',
    deleting: 'Deleting…',
    deleteNameThisActionIsPermanent: 'Delete “{{name}}”? This action is permanent.',
    couldNotLoadTheAutomations: 'Could not load the automations.',
    couldNotEdit: 'Could not edit.',
    unnamed: '(unnamed)',
    actions: 'Actions',
    status: 'Status',
    edit: 'Edit',
    automateYourCrmWhenAContact: 'Automate your CRM: when a contact is created, when a field or stage changes, or when there is no reply — enrich, assign, group, notify or draft.',
    newAutomation: '+ New automation',
    tryAgain: 'Try again',
    noCrmAutomation: 'No CRM automation',
    createYourFirstRuleChooseA: 'Create your first rule: choose a trigger, add actions and connect them in the visual editor.',
    createAnAutomation: '+ Create an automation',
  },
  flow: {
    agentTask: 'Agent task',
    llmCallWithAPromptAgent: 'LLM call with a prompt (agent).',
    toolCall: 'Tool call',
    runsAnMcpToolFromThe: 'Runs an MCP tool from the registry.',
    approval: 'Approval',
    putsTheRunOnHoldFor: 'Puts the run on hold for a human (HIL).',
    condition: 'Condition',
    trueFalseBranchBasedOnAn: 'True/false branch based on an expression.',
    parallel: 'Parallel',
    activatesAllItsOutgoingBranches: 'Activates all its outgoing branches.',
    delay: 'Delay',
    waitsForANumberOfSeconds: 'Waits for a number of seconds.',
    notification: 'Notification',
    sendsAMessageToTheUser: 'Sends a message to the user.',
    loop: 'Loop',
    iteratesAnActionOverACollection: 'Iterates an action over a collection (fan-out).',
    webhookTrigger: 'Webhook trigger',
    entryPointLaunchedByAnIncoming: 'Entry point: launched by an incoming webhook call.',
    scheduledTrigger: 'Scheduled trigger',
    entryPointLaunchedOnACadence: 'Entry point: launched on a cadence (cron / RRULE).',
    eventTrigger: 'Event trigger',
    entryPointLaunchedByAnEvent: 'Entry point: launched by an event (e.g. contact.created).',
    enroll: 'Enroll',
    enrollsAnAudienceSegmentInThe: 'Enrolls an audience/segment in the journey.',
    waitForEvent: 'Wait for event',
    pausesTheJourneyUntilAnEvent: 'Pauses the journey until an event.',
    behaviorBranch: 'Behavior branch',
    branchesByBehaviorOpenedClickedElse: 'Branches by behavior (opened / clicked / else).',
  },
  triggersPanel: {
    webhook: 'Webhook',
    scheduledCron: 'Scheduled (cron)',
    couldNotCreateTheTrigger: 'Could not create the trigger.',
    newTrigger: 'New trigger',
    type: 'Type',
    webhookIncomingUrl: 'Webhook (incoming URL)',
    scheduledCronRrule: 'Scheduled (cron / RRULE)',
    event: 'Event',
    nameOptional: 'Name (optional)',
    dailyReminder: 'Daily reminder',
    aSecretUrlIsGeneratedOn: 'A secret URL is generated on creation. The call\'s JSON body becomes the run\'s inputs.',
    cron: 'Cron',
    rrule: 'RRULE',
    cronExpression5Fields: 'Cron expression (5 fields)',
    rruleIcalendar: 'RRULE (iCalendar)',
    freqDailyByhour9Byminute0: 'FREQ=DAILY;BYHOUR=9;BYMINUTE=0',
    timeZoneIanaOptional: 'Time zone (IANA, optional)',
    americaToronto: 'America/Toronto',
    eventName: 'Event name',
    contactCreated: 'contact.created',
    creating: 'Creating…',
    createTheTrigger: 'Create the trigger',
    couldNotTrigger: 'Could not trigger.',
    deleteTheTrigger: 'Delete the trigger',
    permanentlyDeleteThisTypeLabelTrigger: 'Permanently delete this {{TYPE_LABEL}} trigger?',
    delete: 'Delete',
    unnamed: 'unnamed',
    fireCountTriggerRunS: '· {{fire_count}} trigger run(s)',
    triggerNow: 'Trigger now',
    publishTheWorkflowForAReal: 'Publish the workflow for a real run',
    test: 'Test',
    incomingUrlSecret: 'Incoming URL (secret)',
    copy: 'Copy',
    cadence: 'Cadence:',
    next: '· next:',
    event2: 'Event:',
    lastTriggeredV: 'Last triggered: {{v}}',
    aiActivity: 'AI activity →',
    couldNotLoadTheTriggers: 'Could not load the triggers.',
    workflowTriggers: 'Workflow triggers',
    close: 'Close',
    thisWorkflowIsNotPublishedYet: 'This workflow is not published yet: a trigger can be saved, but a trigger run will only start a real run once the workflow is published.',
    createTestOrDeleteATrigger: 'create, test or delete a trigger',
    loading: 'Loading…',
    noTriggerCreateOneAboveTo: 'No trigger. Create one above to launch this workflow by webhook, schedule or event.',
  },
  widgets: {
    justNow: 'just now',
    sSAgo: '{{s}} s ago',
    mMinAgo: '{{m}} min ago',
    hHAgo: '{{h}} h ago',
    vDAgo: '{{v}} d ago',
    noData: 'No data',
  },
  workflowBuilderPage: {
    runError: 'error',
    runOk: 'ok',
    emptyPrompt: 'empty prompt',
    noTool: 'no tool',
    noExpression: 'no expression',
    noMessage: 'no message',
    parallelBranches: 'parallel branches',
    lengthItems: '{{length}} items',
    webhookInput: 'webhook input',
    scheduled: 'scheduled',
    event: 'event',
    enrollment: 'enrollment',
    waitingForEvent: 'waiting for event',
    actorV: 'Actor: {{v}}',
    jsonConfiguration: 'JSON configuration',
    aJsonObjectIsExpected: 'A JSON object is expected.',
    invalidJson: 'Invalid JSON.',
    theNodeTestFailed: 'The node test failed.',
    testThisNode: 'Test this node',
    testInProgress: 'Test in progress…',
    testThisNode2: '▶ Test this node',
    runsThisNodeInIsolationTool: 'Runs this node in isolation (tool calls protected / notifications captured).',
    preview: 'preview',
    agentPersona: 'Agent / persona',
    none: '— none —',
    personasAiStaff: 'Personas (AI staff)',
    agentsRegistry: 'Agents (registry)',
    chooseAnAiTeamMemberPersona: 'Choose an AI team member (persona) or a registry agent. The chosen actor runs the task.',
    promptTemplate: 'Prompt (template)',
    insertATemplate: '— insert a template —',
    prompt: 'Prompt',
    writeAPostBasedOnInputs: 'Write a post based on {inputs[topic]}…',
    references: 'References:',
    inputsChamp: '{inputs[champ]}',
    nodeIdText: '{node_id[text]}',
    taskTypeTaskType: 'Task type (task_type)',
    generation: 'generation',
    tool: 'Tool',
    chooseATool: '— choose a tool —',
    riskRiskLevel: 'Risk: {{risk_level}}',
    argumentsJson: 'Arguments (JSON)',
    stringsAreRenderedWithTheContext: 'Strings are rendered with the context (',
    inputsX: '{inputs[x]}',
    expression: 'Expression',
    ctxNodeIdFieldOk: 'ctx[\'node_id\'][\'field\'] == "ok"',
    operatorsContainsThenConnectTheEdges: 'Operators: == != > < >= <= contains. Then connect the edges',
    true: 'true',
    false: 'false',
    durationSeconds: 'Duration (seconds)',
    n30SInlineWaitBeyondThat: '≤ 30 s: inline wait. Beyond that: the run is paused, then resumed.',
    message: 'Message',
    thePostForInputsTopicIs: 'The post for {inputs[topic]} is ready.',
    actionTypeActionType: 'Action type (action_type)',
    publish: 'publish',
    messageOptional: 'Message (optional)',
    theRunGoesInto: 'The run goes into',
    awaitingApproval: 'awaiting approval',
    untilAHumanDecidesHil: 'until a human decides (HIL).',
    entryPoint: 'Entry point',
    webhook: 'webhook',
    theWorkflowStartsWhenAnIncoming: ': the workflow starts when an incoming HTTP call is received, whose JSON body becomes the run\'s inputs (referenced via',
    openThe: 'Open the',
    triggers: 'Triggers',
    tabToolbarToCreateAWebhook: 'tab (toolbar) to create a webhook trigger and copy its secret URL.',
    cronExpression5Fields: 'Cron expression (5 fields)',
    orAnIcalendarRruleBelowThe: 'Or an iCalendar RRULE below. The actual cadence is carried by the scheduled trigger (tab',
    rruleAlternative: 'RRULE (alternative)',
    freqDailyByhour9Byminute0: 'FREQ=DAILY;BYHOUR=9;BYMINUTE=0',
    timeZoneIana: 'Time zone (IANA)',
    americaToronto: 'America/Toronto',
    eventName: 'Event name',
    contactCreated: 'contact.created',
    theWorkflowStartsWhenThisEvent: 'The workflow starts when this event is broadcast. Save an event trigger (tab',
    withTheSameName: ') with the same name.',
    audienceOneEntryPerLine: 'Audience (one entry per line)',
    inputsContactContact123: '{inputs[contact]}\ncontact-123',
    eachLineIsRenderedWithThe: 'Each line is rendered with the context (',
    segmentOptional: 'Segment (optional)',
    newProspects: 'new-prospects',
    expectedEvent: 'Expected event',
    purchased: 'purchased',
    theJourneyPausesUntilThisEvent: 'The journey pauses until this event occurs (or is already present in the inputs',
    events: 'events',
    signalBehavior: 'Signal (behavior)',
    inputsBehavior: '{inputs[behavior]}',
    connectTheOutgoingEdgesWithA: 'Connect the outgoing edges with a behavior label (e.g.',
    opened: 'opened',
    clicked: 'clicked',
    else: 'else',
    onlyTheEdgeWhoseLabelEquals: '): only the edge whose label equals the evaluated signal fires.',
    itemsSourcePath: 'Items source (path)',
    inputsContacts: 'inputs.contacts',
    pathInTheRunContextE: 'Path in the run context (e.g.',
    triggerPayloadContacts: 'trigger.payload.contacts',
    theActionBelowRunsOncePer: '). The action below runs once per item.',
    itemVariable: 'Item variable',
    indexVariable: 'Index variable',
    maxIterations1000: 'Max iterations (≤ 1000)',
    stopOn1stFailure: 'Stop on 1st failure',
    actionPerItem: 'Action per item',
    actionSMcpTool: 'Action\'s MCP tool',
    mcpToolName: 'MCP tool name',
    actionSJsonArguments: 'Action\'s JSON arguments',
    emailItemEmail: '{\n  "email": "{item[email]}"\n}',
    jsonArgumentsReferToTheCurrent: 'JSON arguments; refer to the current item via',
    itemChamp: '{item[champ]}',
    perItemActionPrompt: 'Per-item action prompt',
    writeAMessageForItemName: 'Write a message for {item[name]}…',
    perItemNotificationMessage: 'Per-item notification message',
    notifiedForItemName: 'Notified for {item[name]}',
    perItemConditionExpression: 'Per-item condition expression',
    itemScore50: '{item[score]} > 50',
    thisNodeHasNoConfigurationAll: 'This node has no configuration: all its outgoing edges become active and run sequentially (pseudo-parallel v1).',
    resilience: 'Resilience',
    onErrorOnError: 'On error (on_error)',
    failTheRun: 'Fail the run',
    continue: 'Continue',
    branchErrorEdge: 'Branch (error edge)',
    drawAnEdgeFromThisNode: 'Draw an edge from this node and mark it “error” in the edge panel.',
    maxAttempts: 'Max attempts',
    backoffS: 'Backoff (s)',
    edge: 'Edge',
    delete: 'Delete',
    branchCondition: 'Branch (condition)',
    noLabel: '— no label —',
    aConditionNodeMustHaveAt: 'A condition node must have at least one edge',
    or: 'or',
    behaviorLabel: 'Behavior label',
    openedClickedElse: 'opened / clicked / else',
    theEdgeFiresWhenTheNode: 'The edge fires when the node\'s evaluated signal equals this label.',
    catchesTheUncoveredCases: 'catches the uncovered cases.',
    errorEdge: 'Error edge (',
    onErrorBranch: 'on_error = branch',
    checkSoThisEdgeOnlyFires: 'Check so this edge only fires when the source node fails (its',
    onError: 'on_error',
    mustBeSetTo: 'must be set to',
    branch: 'Branch',
    otherwiseItIsASimpleEdge: '). Otherwise, it is a simple edge without a label.',
    configuration: 'Configuration',
    selectANodeOrAnEdge: 'Select a node or an edge to edit it. Drag a type from the palette to add a node.',
    name: 'Name',
    id: 'id:',
    actions: 'Actions',
    flowLogic: 'Flow logic',
    journey: 'Journey',
    human: 'Human',
    palette: 'Palette',
    drag: 'drag →',
    readOnlyOnlyDraftsCanBe: 'Read-only: only drafts can be edited.',
    theInputsMustBeAJson: 'The inputs must be a JSON object.',
    couldNotRunIsTheWorkflow: 'Could not run (is the workflow published?).',
    runTheWorkflow: 'Run the workflow',
    viewInAiActivity: 'View in AI Activity →',
    cancel: 'Cancel',
    launching: 'Launching…',
    run: 'Run',
    runStarted: 'Run started:',
    inputsJson: 'Inputs (JSON)',
    availableInPromptsVia: 'Available in prompts via',
    queued: 'queued',
    inProgress: 'in progress',
    awaitingApproval2: 'awaiting approval',
    paused: 'paused',
    completed: 'completed',
    failed: 'failed',
    cancelled: 'cancelled',
    recentRuns: 'Recent runs',
    startedFromThisEditor: 'started from this editor',
    active: 'active',
    completed2: 'completed',
    failures: 'failures',
    tokens: 'tokens',
    cost: 'cost',
    aiActivity: 'AI activity →',
    clearTheLocalListDoesNot: 'Clear the local list (does not cancel the runs)',
    erase: 'Erase',
    closeTheRunsPanel: 'Close the runs panel',
    noRunStartedFromThisEditor: 'No run started from this editor. Publish, then click “Run”.',
    loading: 'loading…',
    unavailable: 'unavailable',
    saving: 'Saving…',
    save: 'Save',
    validateTheSpec: 'Validate the spec',
    run2: 'Run',
    publishTheWorkflowToRunIt: 'Publish the workflow to run it',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitToScreen: 'Fit to screen',
    couldNotSave: 'Could not save.',
    couldNotValidate: 'Could not validate.',
    couldNotPublish: 'Could not publish.',
    deleteTheWorkflow: 'Delete the workflow',
    archiveTheWorkflow: 'Archive the workflow',
    permanentlyDeleteNameThisCannotBe: 'Permanently delete “{{name}}”? This cannot be undone.',
    nameIsPublishedItWillBe: '“{{name}}” is published: it will be archived (removed from the active list).',
    archive: 'Archive',
    couldNotDelete: 'Could not delete.',
    workflows: '← Workflows',
    workflowName: 'Workflow name',
    unsavedChanges: 'unsaved changes',
    validate: 'Validate',
    publish2: 'Publish',
    publishTheWorkflowFirst: 'Publish the workflow first',
    triggersWebhookScheduledEvent: 'Triggers (webhook / scheduled / event)',
    runs: 'Runs',
    specValidTheWorkflowCanBe: '✓ Spec valid — the workflow can be published and run.',
    specErrorS: 'spec error(s):',
    backspace: 'Backspace',
    delete2: 'Delete',
    workflowNotFound: 'Workflow not found.',
    backToWorkflows: '← Back to workflows',
    loadingTheEditor: 'Loading the editor…',
    automationsWorkflows: 'Automations & workflows',
    simulationNoRealCall: 'Simulation (no real call)',
    simulationHintDryRun: 'Simulation: tool, AI, enrolment and mailbox calls are listed without being executed.',
    simulationHintRealRun: 'Real run: the called tools will really act (confirmation required).',
    simulationBadge: 'simulation',
    realNodeRunTitle: 'Real node run',
    realNodeRunMessage: 'The node will run for real: the called tools (publishing, sending, CRM writes…) will really act and the AI will consume credits. Continue?',
    runForReal: 'Run for real',
    simulatedCallsCount: 'Simulated calls ({{count}})',
    noExternalEffect: 'No external effect: this node calls nothing.',
  },
  workflowsListPage: {
    published: 'Published',
    archived: 'Archived',
    draft: 'Draft',
    couldNotCreateTheWorkflow: 'Could not create the workflow.',
    newWorkflow: 'New workflow',
    cancel: 'Cancel',
    creating: 'Creating…',
    createAndEdit: 'Create and edit',
    name: 'Name',
    assistedLinkedinPublishing: 'Assisted LinkedIn publishing',
    description: 'Description',
    optional: '(optional)',
    whatThisWorkflowAutomates: 'What this workflow automates…',
    couldNotLoadTheTemplates: 'Could not load the templates.',
    couldNotCreateTheWorkflowFrom: 'Could not create the workflow from this template.',
    templateGallery: 'Template gallery',
    noTemplateAvailable: 'No template available.',
    stepsStepS: '{{steps}} step(s)',
    useThisTemplate: 'Use this template',
    noStepsEmptyEditor: 'No steps — empty editor',
    delete: 'Delete',
    archive: 'Archive',
    updatedV: 'Updated {{v}}',
    edit: 'Edit →',
    open: 'Open →',
    steps: 'steps',
    noDescription: 'No description',
    couldNotLoadTheWorkflows: 'Could not load the workflows.',
    couldNotPublishIsTheSpec: 'Could not publish (is the spec valid?).',
    deleteTheWorkflow: 'Delete the workflow',
    archiveTheWorkflow: 'Archive the workflow',
    permanentlyDeleteNameThisCannotBe: 'Permanently delete “{{name}}”? This cannot be undone.',
    nameIsPublishedItWillBe: '“{{name}}” is published: it will be archived (removed from the active list).',
    couldNotDelete: 'Could not delete.',
    status: 'Status',
    version: 'Version',
    steps2: 'Steps',
    updated: 'Updated',
    actions: 'Actions',
    edit2: 'Edit',
    open2: 'Open',
    publishing: 'Publishing…',
    publish: 'Publish',
    automationsWorkflows: 'Automations & workflows',
    workflows: 'Workflows',
    yourAutomationsAgentsDragAndDrop: 'Your automations: agents (drag-and-drop, publish, run) and CRM.',
    models: 'Models',
    newWorkflow2: '+ New workflow',
    automationType: 'Automation type',
    agentWorkflows: 'Agent workflows',
    crmAutomations: 'CRM automations',
    tryAgain: 'Try again',
    published2: 'Published',
    drafts: 'Drafts',
    totalSteps: 'Total steps',
    active: 'Active',
    archived2: 'Archived',
    workflowView: 'Workflow view',
    cards: 'Cards',
    table: 'Table',
    loadingWorkflows: 'Loading workflows',
    loading: 'Loading…',
    noWorkflowYet: 'No workflow yet',
    createYourFirstWorkflowAndAssemble: 'Create your first workflow and assemble its steps (agent tasks, tools, conditions, approvals…) in the drag-and-drop visual editor.',
    createAWorkflow: '+ Create a workflow',
    readyToRun: 'ready to run',
  },
};

export default en;
